import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import type { FundamentalData } from '../types/finance'
import { getFundamentals, isUsingMockData } from '../lib/finance-api'
import MockDataBanner from '../components/finance/MockDataBanner'

function fmt(n: number | null, opts?: { style?: string; currency?: string; pct?: boolean; compact?: boolean }): string {
  if (n === null || n === undefined) return 'N/A'
  if (opts?.pct) return `${(n * 100).toFixed(2)}%`
  if (opts?.compact) {
    if (Math.abs(n) >= 1e12) return `$${(n / 1e12).toFixed(2)}T`
    if (Math.abs(n) >= 1e9) return `$${(n / 1e9).toFixed(2)}B`
    if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(2)}M`
  }
  if (opts?.currency) return `$${n.toFixed(2)}`
  return n.toFixed(2)
}

export default function FundamentalsPage() {
  const { symbol } = useParams<{ symbol: string }>()
  const [data, setData] = useState<FundamentalData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [isMock, setIsMock] = useState(false)

  useEffect(() => {
    if (!symbol) return
    setLoading(true)
    setError(null)
    getFundamentals(symbol)
      .then((result) => { setData(result); setIsMock(isUsingMockData()) })
      .catch(() => setError(`Failed to load fundamentals for ${symbol}`))
      .finally(() => setLoading(false))
  }, [symbol])

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl p-6 animate-pulse">
            <div className="h-6 bg-slate-200 dark:bg-slate-700 rounded w-1/4 mb-4" />
            <div className="grid grid-cols-3 gap-4">
              {[1, 2, 3, 4, 5, 6].map((j) => <div key={j} className="h-14 bg-slate-200 dark:bg-slate-700 rounded" />)}
            </div>
          </div>
        ))}
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 text-red-600 dark:text-red-400 rounded-xl px-4 py-3 text-sm">
        {error || 'No data available'}
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <Link to="/dashboard/financial-app" className="text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </Link>
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">{data.symbol}</h1>
            <span className="px-2 py-0.5 bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400 text-xs rounded">{data.sector}</span>
          </div>
          <p className="text-slate-500 dark:text-slate-400 text-sm">{data.name} — Fundamental Data</p>
        </div>
      </div>

      <MockDataBanner visible={isMock} />

      <div className="space-y-6">
        {/* Valuation */}
        <Section title="Valuation">
          <Metric label="Market Cap" value={fmt(data.marketCap, { compact: true })} />
          <Metric label="Enterprise Value" value={fmt(data.enterpriseValue, { compact: true })} />
          <Metric label="P/E Ratio" value={fmt(data.peRatio)} />
          <Metric label="Forward P/E" value={fmt(data.forwardPE)} />
          <Metric label="PEG Ratio" value={fmt(data.pegRatio)} />
          <Metric label="Price / Book" value={fmt(data.priceToBook)} />
          <Metric label="Price / Sales" value={fmt(data.priceToSales)} />
        </Section>

        {/* Profitability */}
        <Section title="Profitability">
          <Metric label="Revenue" value={fmt(data.revenue, { compact: true })} />
          <Metric label="Revenue Growth" value={fmt(data.revenueGrowth, { pct: true })} />
          <Metric label="Net Income" value={fmt(data.netIncome, { compact: true })} />
          <Metric label="EPS (TTM)" value={fmt(data.eps, { currency: 'USD' })} />
          <Metric label="Forward EPS" value={fmt(data.forwardEps, { currency: 'USD' })} />
          <Metric label="Profit Margin" value={fmt(data.profitMargin, { pct: true })} />
          <Metric label="Operating Margin" value={fmt(data.operatingMargin, { pct: true })} />
          <Metric label="ROE" value={fmt(data.roe, { pct: true })} />
          <Metric label="ROA" value={fmt(data.roa, { pct: true })} />
        </Section>

        {/* Balance Sheet */}
        <Section title="Balance Sheet">
          <Metric label="Total Cash" value={fmt(data.totalCash, { compact: true })} />
          <Metric label="Total Debt" value={fmt(data.totalDebt, { compact: true })} />
          <Metric label="Debt / Equity" value={fmt(data.debtToEquity)} />
          <Metric label="Current Ratio" value={fmt(data.currentRatio)} />
          <Metric label="Book Value / Share" value={fmt(data.bookValue, { currency: 'USD' })} />
        </Section>

        {/* Dividends */}
        <Section title="Dividends">
          <Metric label="Dividend Yield" value={fmt(data.dividendYield, { pct: true })} />
          <Metric label="Dividend Rate" value={fmt(data.dividendRate, { currency: 'USD' })} />
          <Metric label="Payout Ratio" value={fmt(data.payoutRatio, { pct: true })} />
          <Metric label="Ex-Dividend Date" value={data.exDividendDate || 'N/A'} />
        </Section>

        {/* Trading */}
        <Section title="Trading Info">
          <Metric label="52-Week High" value={fmt(data.fiftyTwoWeekHigh, { currency: 'USD' })} />
          <Metric label="52-Week Low" value={fmt(data.fiftyTwoWeekLow, { currency: 'USD' })} />
          <Metric label="50-Day Avg" value={fmt(data.fiftyDayAvg, { currency: 'USD' })} />
          <Metric label="200-Day Avg" value={fmt(data.twoHundredDayAvg, { currency: 'USD' })} />
          <Metric label="Avg Volume" value={data.avgVolume ? `${(data.avgVolume / 1e6).toFixed(2)}M` : 'N/A'} />
          <Metric label="Shares Outstanding" value={data.sharesOutstanding ? `${(data.sharesOutstanding / 1e9).toFixed(2)}B` : 'N/A'} />
          <Metric label="Float" value={data.floatShares ? `${(data.floatShares / 1e9).toFixed(2)}B` : 'N/A'} />
          <Metric label="Short Ratio" value={fmt(data.shortRatio)} />
        </Section>
      </div>

      {/* Link to options */}
      <div className="mt-8 text-center">
        <Link
          to={`/dashboard/financial-app/options/${data.symbol}`}
          className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors"
        >
          View Options Chain
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Link>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-700">
        <h2 className="text-slate-900 dark:text-white font-semibold">{title}</h2>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-px bg-slate-200 dark:bg-slate-700">
        {children}
      </div>
    </div>
  )
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-white dark:bg-slate-800 p-4">
      <p className="text-slate-500 text-xs mb-1">{label}</p>
      <p className="text-slate-900 dark:text-white text-sm font-semibold">{value}</p>
    </div>
  )
}
